import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { selectV2 } from '../v2Upload.slice';
import { STEP_LABELS } from '../stepLabels';

// idx, not location.key: creating a draft replaces the entry, leaving the
// wizard first in history with a fresh key.
function leaveWizard(navigate: NavigateFunction) {
  const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
  if (idx > 0) navigate(-1);
  else navigate('/');
}

function BackButton({
  onBack,
  disabled,
}: {
  onBack?: () => void;
  disabled: boolean;
}) {
  const navigate = useNavigate();
  return (
    <Button
      variant="outline"
      className="gap-1.5"
      onClick={onBack ?? (() => leaveWizard(navigate))}
      disabled={disabled}
    >
      <ChevronLeft className="w-4 h-4" />
      Back
    </Button>
  );
}

function ContinueButton({
  label,
  short,
  busy,
  disabled,
  onClick,
}: {
  label: string;
  short: string;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      className="gap-2 shadow-brand disabled:shadow-none"
    >
      {busy && (
        <span className="w-3.5 h-3.5 rounded-full border-2 border-current/30 border-t-current animate-spin" />
      )}
      {/* The full label beside Back overflows a phone row. */}
      <span className="max-sm:hidden">{label}</span>
      <span className="sm:hidden">{short}</span>
      {!busy && <ArrowRight className="w-4 h-4" />}
    </Button>
  );
}

/** What Continue reads at each width. The full label names the step it goes to;
 * beside Back it overflows a phone row, so the short one drops that half. */
function continueText({
  busy,
  busyLabel,
  continueLabel,
  step,
}: {
  busy: boolean;
  busyLabel: string;
  continueLabel?: string;
  step: number;
}): { label: string; short: string } {
  if (busy) return { label: busyLabel, short: busyLabel };
  if (continueLabel) return { label: continueLabel, short: continueLabel };
  return { label: `Continue to ${STEP_LABELS[step + 1]}`, short: 'Continue' };
}

/** The wizard's bottom bar: Back, the step's own gate hint, and a Continue that
 * names where it goes. `busy` blocks Continue on its own, but Back is left to
 * `backDisabled` — a step's sync blocks going back, the G-code parse doesn't. */
export default function WizardFooter({
  onBack,
  onContinue,
  busy,
  busyLabel,
  backDisabled,
  continueDisabled,
  continueLabel,
  hint,
}: {
  onBack?: () => void;
  onContinue: () => void;
  busy: boolean;
  busyLabel: string;
  backDisabled: boolean;
  continueDisabled: boolean;
  continueLabel?: string;
  hint?: ReactNode;
}) {
  const { step } = useSelector(selectV2);
  const { label, short } = continueText({
    busy,
    busyLabel,
    continueLabel,
    step,
  });

  return (
    <>
      {/* mt-auto on the spacer, not the bar: it pushes the bar to the bottom of
          a short step, and leaves the last field clear of it on a tall one. Both
          need the step's root to be `flex flex-col min-h-full`. */}
      <div className="mt-auto h-8 flex-none" />
      {/* -mx-7 cancels the wizard's own padding so the bar is full-bleed. */}
      <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-7 flex flex-wrap items-center gap-3 sm:gap-4 border-t bg-card px-4 sm:px-7 py-4 shadow-[0_-6px_16px_-12px_hsl(var(--shadow-color)/0.35)]">
        <BackButton onBack={onBack} disabled={backDisabled} />
        <span className="grow" />
        {/* Between the buttons the hint wrapped to four lines. */}
        <span className="empty:hidden max-sm:order-first max-sm:w-full max-sm:text-center">
          {hint}
        </span>
        <span data-focus="continue">
          <ContinueButton
            label={label}
            short={short}
            busy={busy}
            disabled={busy || continueDisabled}
            onClick={onContinue}
          />
        </span>
      </div>
    </>
  );
}
